import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Star, Plus, Activity, Ruler, FileText, X, Edit, Trash2 } from 'lucide-react';
import AddHealthRecordModal from './AddHealthRecordModal';

const ClientEvolutionModal = ({ client, open, onClose }) => {
  const [evolution, setEvolution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [showAddProducto, setShowAddProducto] = useState(false);
  const [showAddCompra, setShowAddCompra] = useState(false);
  const [showEditProducto, setShowEditProducto] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [faseActual, setFaseActual] = useState(client?.fase_actual || '0');
  const [productoForm, setProductoForm] = useState({
    fecha_visita: '',
    nombre: '',
    dosis: '',
    indicaciones: '',
    notas: ''
  });
  const [compraForm, setCompraForm] = useState({
    fecha_compra: '',
    producto: '',
    cantidad: 1,
    precio: '',
    notas: ''
  });

  useEffect(() => {
    if (open && client) {
      loadEvolution();
      setFaseActual(client?.fase_actual || '0');
    }
  }, [open, client]);

  const loadEvolution = async () => {
    try {
      const api = createAuthAPI();
      const response = await api.get(`/admin/clients/${client.id}/evolution`);
      setEvolution(response.data);
    } catch (error) {
      toast.error('Error al cargar evolución');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeFase = async (newFase) => {
    try {
      const api = createAuthAPI();
      await api.put(`/admin/clients/${client.id}/fase`, { fase: newFase });
      setFaseActual(newFase);
      toast.success(`Fase actualizada a: Fase ${newFase}`);
      // Reload evolution to get updated data
      loadEvolution();
    } catch (error) {
      console.error('Error changing fase:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar la fase');
    }
  };


  const handleAddProducto = async (e) => {
    e.preventDefault();
    try {
      const api = createAuthAPI();
      const data = {
        client_id: client.id,
        fecha_visita: productoForm.fecha_visita,
        nombre: productoForm.nombre,
        dosis: productoForm.dosis || null,
        indicaciones: productoForm.indicaciones || null,
        notas: productoForm.notas || null
      };
      await api.post(`/admin/clients/${client.id}/productos-recomendados`, data);
      toast.success('Producto recomendado añadido exitosamente');
      setShowAddProducto(false);
      setProductoForm({
        fecha_visita: '',
        nombre: '',
        dosis: '',
        indicaciones: '',
        notas: ''
      });
      loadEvolution();
    } catch (error) {
      console.error('Error adding producto:', error);
      toast.error(error.response?.data?.detail || 'Error al añadir producto recomendado');
    }
  };

  const handleDeleteProducto = async (productoId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta recomendación?')) return;
    
    try {
      const api = createAuthAPI();
      await api.delete(`/admin/clients/${client.id}/productos-recomendados/${productoId}`);
      toast.success('Producto eliminado');
      loadEvolution();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const openEditProducto = (producto) => {
    setEditingProducto(producto);
    setProductoForm({
      fecha_visita: producto.fecha_visita.split('T')[0], // Solo la fecha sin hora
      nombre: producto.nombre,
      dosis: producto.dosis || '',
      indicaciones: producto.indicaciones || '',
      notas: producto.notas || ''
    });
    setShowEditProducto(true);
  };

  const handleUpdateProducto = async (e) => {
    e.preventDefault();
    try {
      const api = createAuthAPI();
      await api.put(`/admin/clients/${client.id}/productos-recomendados/${editingProducto.id}`, productoForm);
      toast.success('Producto actualizado');
      setShowEditProducto(false);
      setEditingProducto(null);
      setProductoForm({
        fecha_visita: '',
        nombre: '',
        dosis: '',
        indicaciones: '',
        notas: ''
      });
      loadEvolution();
    } catch (error) {
      toast.error('Error al actualizar producto');
    }
  };

  const handleAddCompra = async (e) => {
    e.preventDefault();
    try {
      const api = createAuthAPI();
      const data = {
        client_id: client.id,
        fecha_compra: compraForm.fecha_compra,
        producto: compraForm.producto,
        cantidad: compraForm.cantidad || 1,
        precio: compraForm.precio ? parseFloat(compraForm.precio) : null,
        notas: compraForm.notas || null
      };
      await api.post(`/admin/clients/${client.id}/productos-comprados`, data);
      toast.success('Compra registrada exitosamente');
      setShowAddCompra(false);
      setCompraForm({
        fecha_compra: '',
        producto: '',
        cantidad: 1,
        precio: '',
        notas: ''
      });
      loadEvolution();
    } catch (error) {
      console.error('Error adding compra:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar compra');
    }
  };

  const handleDeleteCompra = async (compraId) => {
    if (!window.confirm('¿Estás seguro de eliminar este registro de compra?')) return;
    
    try {
      const api = createAuthAPI();
      await api.delete(`/admin/clients/${client.id}/productos-comprados/${compraId}`);
      toast.success('Compra eliminada');
      loadEvolution();
    } catch (error) {
      toast.error('Error al eliminar compra');
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <p>Cargando...</p>
        </DialogContent>
      </Dialog>
    );
  }

  const weightData = evolution?.weight_records.map(record => {
    // Calculate cardiovascular index if both Tg and HDL exist
    const indiceCardiovascular = (record.trigliceridos && record.colesterol_hdl) 
      ? (record.trigliceridos / record.colesterol_hdl).toFixed(2)
      : null;
    
    return {
      fecha: new Date(record.fecha).toLocaleDateString('es-ES'),
      peso: record.peso,
      glucemia: record.glucemia,
      colesterol: record.colesterol,
      colesterol_hdl: record.colesterol_hdl,
      colesterol_ldl: record.colesterol_ldl,
      trigliceridos: record.trigliceridos,
      indiceCardiovascular: indiceCardiovascular ? parseFloat(indiceCardiovascular) : null,
      // Anthropometrics
      circunferencia_cintura: record.circunferencia_cintura,
      circunferencia_cadera: record.circunferencia_cadera,
      circunferencia_pecho: record.circunferencia_pecho,
      circunferencia_brazo: record.circunferencia_brazo,
      circunferencia_muslo: record.circunferencia_muslo,
      // Body composition
      porcentaje_grasa: record.porcentaje_grasa,
      masa_muscular: record.masa_muscular,
      indice_visceral: record.indice_visceral
    };
  }) || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-green-800">
            Evolución: {client.nombre} {client.apellidos}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="weight" className="mt-4">
          <TabsList className="grid w-full" style={{gridTemplateColumns: 'repeat(5, 1fr)'}}>
            <TabsTrigger value="weight">Peso</TabsTrigger>
            <TabsTrigger value="products">Productos</TabsTrigger>
            <TabsTrigger value="analytics">Analíticas</TabsTrigger>
            <TabsTrigger value="anthropometrics">Antropometría</TabsTrigger>
            <TabsTrigger value="composition">Composición</TabsTrigger>
          </TabsList>

          {/* Weight Tab */}
          <TabsContent value="weight" className="space-y-6">
            {/* Botón Nuevo Registro y Selector de Fase */}
            <div className="flex justify-between items-center">
              <Button 
                onClick={() => setShowAddRecord(true)} 
                className="pharmacy-button text-white flex items-center gap-2"
                data-testid="add-record-button"
                size="lg"
              >
                <Plus className="h-5 w-5" />
                Nuevo Registro
              </Button>
              
              <div className="flex items-center gap-3">
                <Label className="font-semibold text-green-800">Fase de Dieta:</Label>
                <Select value={faseActual} onValueChange={handleChangeFase}>
                  <SelectTrigger className="w-[140px] border-2 border-green-600 focus:ring-green-500">
                    <SelectValue placeholder="Seleccionar fase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Fase 0</SelectItem>
                    <SelectItem value="1a">Fase 1a</SelectItem>
                    <SelectItem value="1b">Fase 1b</SelectItem>
                    <SelectItem value="1c">Fase 1c</SelectItem>
                    <SelectItem value="2a">Fase 2a</SelectItem>
                    <SelectItem value="2b">Fase 2b</SelectItem>
                    <SelectItem value="3">Fase 3 - Estabilización</SelectItem>
                    <SelectItem value="4">Fase 4 - Mantenimiento OMS</SelectItem>
                    <SelectItem value="5">Fase 5 - Detox 10 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Gráfica de Peso */}
            <Card className="pharmacy-card">
              <CardHeader>
                <CardTitle className="text-green-800">📊 Evolución del Peso</CardTitle>
              </CardHeader>
              <CardContent>
                {weightData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={weightData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="fecha" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="peso" stroke="#10b981" strokeWidth={2} name="Peso (kg)" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 py-8">No hay datos de peso registrados</p>
                )}
              </CardContent>
            </Card>

            {/* Tabla de Registros de Peso */}
            <Card className="pharmacy-card">
              <CardHeader>
                <CardTitle className="text-green-800">📋 Tabla de Registros de Peso</CardTitle>
              </CardHeader>
              <CardContent>
                {weightData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="p-3 border text-left font-semibold">Fecha</th>
                          <th className="p-3 border text-left font-semibold">Peso (kg)</th>
                          <th className="p-3 border text-left font-semibold">Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evolution?.weight_records.map((record, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 border">{new Date(record.fecha).toLocaleDateString('es-ES')}</td>
                            <td className="p-3 border font-semibold text-green-700">{record.peso || '-'}</td>
                            <td className="p-3 border text-gray-600">{record.notas || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No hay registros</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Valores de Referencia */}
            <Card className="pharmacy-card bg-blue-50">
              <CardHeader>
                <CardTitle className="text-blue-800">📊 Valores de Referencia (Normales)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">Glucemia</p>
                    <p className="text-blue-600">70-100 mg/dL (ayunas)</p>
                  </div>
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">Colesterol Total</p>
                    <p className="text-blue-600">&lt; 200 mg/dL</p>
                  </div>
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">HDL (Bueno)</p>
                    <p className="text-blue-600">&gt; 40 mg/dL (H) / &gt; 50 (M)</p>
                  </div>
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">LDL (Malo)</p>
                    <p className="text-blue-600">&lt; 100 mg/dL</p>
                  </div>
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">Triglicéridos</p>
                    <p className="text-blue-600">&lt; 150 mg/dL</p>
                  </div>
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">Índice Tg/HDL</p>
                    <p className="text-blue-600">&lt; 3 (óptimo)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="pharmacy-card">
              <CardHeader>
                <CardTitle className="text-green-800">Evolución de Analíticas</CardTitle>
              </CardHeader>
              <CardContent>
                {weightData.length > 0 ? (
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Glucemia y Colesterol Total</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={weightData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="fecha" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="glucemia" stroke="#f59e0b" strokeWidth={2} name="Glucemia (mg/dL)" />
                          <Line type="monotone" dataKey="colesterol" stroke="#ef4444" strokeWidth={2} name="Colesterol Total" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Perfil Lipídico</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={weightData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="fecha" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="colesterol_hdl" stroke="#10b981" strokeWidth={2} name="HDL (Bueno)" />
                          <Line type="monotone" dataKey="colesterol_ldl" stroke="#f59e0b" strokeWidth={2} name="LDL (Malo)" />
                          <Line type="monotone" dataKey="trigliceridos" stroke="#8b5cf6" strokeWidth={2} name="Triglicéridos" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Índice Cardiovascular (Tg/HDL)</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={weightData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="fecha" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="indiceCardiovascular" stroke="#ef4444" strokeWidth={2} name="Índice Tg/HDL" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Tabla de Datos Analíticos */}
                    <div>
                      <h4 className="font-semibold mb-2">Tabla de Valores</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border">
                          <thead className="bg-blue-50">
                            <tr>
                              <th className="p-2 border text-left">Fecha</th>
                              <th className="p-2 border text-left">Glucemia</th>
                              <th className="p-2 border text-left">Col. Total</th>
                              <th className="p-2 border text-left">HDL</th>
                              <th className="p-2 border text-left">LDL</th>
                              <th className="p-2 border text-left">Triglicéridos</th>
                              <th className="p-2 border text-left">Tg/HDL</th>
                            </tr>
                          </thead>
                          <tbody>
                            {weightData.map((record, idx) => (
                              <tr key={idx} className="border-b hover:bg-gray-50">
                                <td className="p-2 border">{record.fecha}</td>
                                <td className="p-2 border">{record.glucemia || '-'}</td>
                                <td className="p-2 border">{record.colesterol || '-'}</td>
                                <td className="p-2 border">{record.colesterol_hdl || '-'}</td>
                                <td className="p-2 border">{record.colesterol_ldl || '-'}</td>
                                <td className="p-2 border">{record.trigliceridos || '-'}</td>
                                <td className="p-2 border">{record.indiceCardiovascular || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No hay datos analíticos registrados</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Anthropometrics Tab */}
          <TabsContent value="anthropometrics" className="space-y-6">
            <Card className="pharmacy-card">
              <CardHeader>
                <CardTitle className="text-green-800">📏 Medidas Antropométricas</CardTitle>
              </CardHeader>
              <CardContent>
                {weightData.length > 0 ? (
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Cintura y Cadera</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={weightData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="fecha" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="circunferencia_cintura" stroke="#f59e0b" strokeWidth={2} name="Cintura (cm)" />
                          <Line type="monotone" dataKey="circunferencia_cadera" stroke="#8b5cf6" strokeWidth={2} name="Cadera (cm)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Pecho, Brazo y Muslo</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={weightData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="fecha" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="circunferencia_pecho" stroke="#10b981" strokeWidth={2} name="Pecho (cm)" />
                          <Line type="monotone" dataKey="circunferencia_brazo" stroke="#3b82f6" strokeWidth={2} name="Brazo (cm)" />
                          <Line type="monotone" dataKey="circunferencia_muslo" stroke="#ec4899" strokeWidth={2} name="Muslo (cm)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Tabla de Medidas */}
                    <div>
                      <h4 className="font-semibold mb-2">📋 Tabla de Medidas</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border">
                          <thead className="bg-yellow-50">
                            <tr>
                              <th className="p-3 border text-left font-semibold">Fecha</th>
                              <th className="p-3 border text-left font-semibold">Cintura (cm)</th>
                              <th className="p-3 border text-left font-semibold">Cadera (cm)</th>
                              <th className="p-3 border text-left font-semibold">Pecho (cm)</th>
                              <th className="p-3 border text-left font-semibold">Brazo (cm)</th>
                              <th className="p-3 border text-left font-semibold">Muslo (cm)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {weightData.map((record, idx) => (
                              <tr key={idx} className="border-b hover:bg-gray-50">
                                <td className="p-3 border">{record.fecha}</td>
                                <td className="p-3 border">{record.circunferencia_cintura || '-'}</td>
                                <td className="p-3 border">{record.circunferencia_cadera || '-'}</td>
                                <td className="p-3 border">{record.circunferencia_pecho || '-'}</td>
                                <td className="p-3 border">{record.circunferencia_brazo || '-'}</td>
                                <td className="p-3 border">{record.circunferencia_muslo || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No hay datos antropométricos registrados</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Body Composition Tab */}
          <TabsContent value="composition" className="space-y-6">
            {/* Valores de Referencia */}
            <Card className="pharmacy-card bg-purple-50">
              <CardHeader>
                <CardTitle className="text-purple-800">⚖️ Valores de Referencia (Normales)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">% Grasa Corporal</p>
                    <p className="text-purple-600">Hombres: 10-20%</p>
                    <p className="text-purple-600">Mujeres: 18-28%</p>
                  </div>
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">Masa Muscular</p>
                    <p className="text-purple-600">Hombres: 40-50%</p>
                    <p className="text-purple-600">Mujeres: 30-40%</p>
                  </div>
                  <div className="bg-white p-3 rounded">
                    <p className="font-semibold text-gray-700">Índice Visceral</p>
                    <p className="text-purple-600">1-12: Normal</p>
                    <p className="text-purple-600">13-59: Alto</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="pharmacy-card">
              <CardHeader>
                <CardTitle className="text-green-800">Composición Corporal</CardTitle>
              </CardHeader>
              <CardContent>
                {weightData.length > 0 ? (
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">% Grasa y Masa Muscular</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={weightData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="fecha" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="porcentaje_grasa" stroke="#f59e0b" strokeWidth={2} name="% Grasa" />
                          <Line type="monotone" dataKey="masa_muscular" stroke="#10b981" strokeWidth={2} name="Masa Muscular (kg)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Índice Visceral</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={weightData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="fecha" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="indice_visceral" stroke="#ef4444" strokeWidth={2} name="Índice Visceral" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Tabla de Datos */}
                    <div>
                      <h4 className="font-semibold mb-2">Tabla de Valores</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border">
                          <thead className="bg-purple-50">
                            <tr>
                              <th className="p-2 border text-left">Fecha</th>
                              <th className="p-2 border text-left">% Grasa</th>
                              <th className="p-2 border text-left">Masa Muscular (kg)</th>
                              <th className="p-2 border text-left">Índice Visceral</th>
                            </tr>
                          </thead>
                          <tbody>
                            {weightData.map((record, idx) => (
                              <tr key={idx} className="border-b hover:bg-gray-50">
                                <td className="p-2 border">{record.fecha}</td>
                                <td className="p-2 border">{record.porcentaje_grasa || '-'}</td>
                                <td className="p-2 border">{record.masa_muscular || '-'}</td>
                                <td className="p-2 border">{record.indice_visceral || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No hay datos de composición corporal registrados</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            {/* Productos Recomendados */}
            <Card className="pharmacy-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-green-800">💊 Productos y Suplementos Recomendados</CardTitle>
                <Button 
                  className="pharmacy-button text-white flex items-center gap-2"
                  onClick={() => setShowAddProducto(true)}
                >
                  <Plus className="h-4 w-4" />
                  Añadir Recomendación
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Registra los productos y suplementos que recomiendas al cliente en cada visita. El cliente podrá verlos en su dashboard.
                  </p>
                  
                  {/* Tabla de productos recomendados por fecha */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="p-3 text-left font-semibold">Fecha Visita</th>
                          <th className="p-3 text-left font-semibold">Producto/Suplemento</th>
                          <th className="p-3 text-left font-semibold">Dosis</th>
                          <th className="p-3 text-left font-semibold">Indicaciones</th>
                          <th className="p-3 text-left font-semibold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evolution?.productos_recomendados && evolution.productos_recomendados.length > 0 ? (
                          evolution.productos_recomendados.map((producto) => (
                            <tr key={producto.id} className="border-t hover:bg-gray-50">
                              <td className="p-3">{new Date(producto.fecha_visita).toLocaleDateString('es-ES')}</td>
                              <td className="p-3 font-semibold text-green-700">{producto.nombre}</td>
                              <td className="p-3">{producto.dosis || '-'}</td>
                              <td className="p-3">{producto.indicaciones || '-'}</td>
                              <td className="p-3">
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEditProducto(producto)}
                                    className="flex items-center gap-1"
                                  >
                                    <Edit className="h-4 w-4" />
                                    Editar
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDeleteProducto(producto.id)}
                                    className="flex items-center gap-1"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Eliminar
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="border-t">
                            <td className="p-3 text-gray-500 text-center" colSpan="5">
                              No hay productos recomendados registrados
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Productos Comprados (PRIVADO - Solo Admin) */}
            <Card className="pharmacy-card border-2 border-blue-200">
              <CardHeader className="flex flex-row items-center justify-between bg-blue-50">
                <div>
                  <CardTitle className="text-blue-800">🛒 Productos Comprados (Privado)</CardTitle>
                  <p className="text-xs text-blue-600 mt-1">⚠️ Esta información es privada y solo visible para ti</p>
                </div>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  onClick={() => setShowAddCompra(true)}
                >
                  <Plus className="h-4 w-4" />
                  Registrar Compra
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Registra los productos que el cliente compra en cada visita. Solo tú puedes ver esta información.
                  </p>
                  
                  {/* Tabla de productos comprados por fecha */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-blue-50">
                        <tr>
                          <th className="p-3 text-left font-semibold">Fecha Compra</th>
                          <th className="p-3 text-left font-semibold">Producto</th>
                          <th className="p-3 text-left font-semibold">Notas</th>
                          <th className="p-3 text-left font-semibold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evolution?.productos_comprados && evolution.productos_comprados.length > 0 ? (
                          evolution.productos_comprados.map((compra) => (
                            <tr key={compra.id} className="border-t hover:bg-gray-50">
                              <td className="p-3">{new Date(compra.fecha_compra).toLocaleDateString('es-ES')}</td>
                              <td className="p-3 font-semibold">{compra.producto}</td>
                              <td className="p-3 text-gray-600">{compra.notas || '-'}</td>
                              <td className="p-3">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteCompra(compra.id)}
                                >
                                  Eliminar
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="border-t">
                            <td className="p-3 text-gray-500 text-center" colSpan="6">
                              No hay compras registradas
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {showAddRecord && (
          <AddHealthRecordModal
            clientId={client.id}
            open={showAddRecord}
            onClose={() => {
              setShowAddRecord(false);
              loadEvolution();
            }}
          />
        )}

        {/* Modal Añadir Producto Recomendado */}
        {showAddProducto && (
          <Dialog open={showAddProducto} onOpenChange={setShowAddProducto}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 pb-4 border-b">
                <DialogTitle className="text-xl font-bold text-green-800">
                  💊 Añadir Producto/Suplemento Recomendado
                </DialogTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddProducto(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogHeader>
              <form onSubmit={handleAddProducto} className="space-y-4 mt-4">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-sm text-blue-700">
                    <strong>ℹ️ Solo el nombre del producto es obligatorio.</strong> Los demás campos son opcionales.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Fecha de Visita *</Label>
                  <Input
                    type="date"
                    value={productoForm.fecha_visita}
                    onChange={(e) => setProductoForm({...productoForm, fecha_visita: e.target.value})}
                    required
                    className="text-base py-6 border-2"
                  />
                </div>
                
                <div className="space-y-2 bg-green-50 p-4 rounded">
                  <Label className="text-base font-semibold">Nombre del Producto/Suplemento *</Label>
                  <Input
                    type="text"
                    value={productoForm.nombre}
                    onChange={(e) => setProductoForm({...productoForm, nombre: e.target.value})}
                    required
                    placeholder="Ej: Omega 3, Vitamina D, Magnesio..."
                    className="text-base py-6 border-2 border-green-300"
                  />
                  <p className="text-xs text-green-700">Este campo es obligatorio</p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Campos opcionales</strong> (déjalos en blanco si no necesitas especificarlos):
                  </p>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-sm">Dosis Recomendada (opcional)</Label>
                      <Input
                        type="text"
                        value={productoForm.dosis}
                        onChange={(e) => setProductoForm({...productoForm, dosis: e.target.value})}
                        placeholder="Ej: 1 cápsula al día"
                        className="text-base py-5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Indicaciones de Uso (opcional)</Label>
                      <Input
                        type="text"
                        value={productoForm.indicaciones}
                        onChange={(e) => setProductoForm({...productoForm, indicaciones: e.target.value})}
                        placeholder="Ej: Tomar con las comidas"
                        className="text-base py-5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Notas Adicionales (opcional)</Label>
                      <Input
                        type="text"
                        value={productoForm.notas}
                        onChange={(e) => setProductoForm({...productoForm, notas: e.target.value})}
                        placeholder="Notas opcionales"
                        className="text-base py-5"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 pharmacy-button text-white">
                    Guardar Recomendación
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddProducto(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Modal Añadir Compra */}
        {showAddCompra && (
          <Dialog open={showAddCompra} onOpenChange={setShowAddCompra}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 pb-4 border-b">
                <div>
                  <DialogTitle className="text-xl font-bold text-blue-800">
                    🛒 Registrar Compra de Producto
                  </DialogTitle>
                  <p className="text-sm text-blue-600">⚠️ Esta información es privada y solo visible para ti</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddCompra(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogHeader>
              <form onSubmit={handleAddCompra} className="space-y-4 mt-4">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-sm text-blue-700">
                    <strong>ℹ️ Solo el nombre del producto es obligatorio.</strong>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Fecha de Compra *</Label>
                  <Input
                    type="date"
                    value={compraForm.fecha_compra}
                    onChange={(e) => setCompraForm({...compraForm, fecha_compra: e.target.value})}
                    required
                    className="text-base py-6 border-2"
                  />
                </div>
                
                <div className="space-y-2 bg-blue-50 p-4 rounded">
                  <Label className="text-base font-semibold">Nombre del Producto *</Label>
                  <Input
                    type="text"
                    value={compraForm.producto}
                    onChange={(e) => setCompraForm({...compraForm, producto: e.target.value})}
                    required
                    placeholder="Nombre del producto comprado"
                    className="text-base py-6 border-2 border-blue-300"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Notas</Label>
                  <Input
                    type="text"
                    value={compraForm.notas}
                    onChange={(e) => setCompraForm({...compraForm, notas: e.target.value})}
                    placeholder="Notas opcionales"
                    className="text-base py-6 border-2"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                    Registrar Compra
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddCompra(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Producto Recomendado Dialog */}
        {showEditProducto && editingProducto && (
          <Dialog open={showEditProducto} onOpenChange={setShowEditProducto}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white shadow-2xl">
              <DialogHeader className="bg-green-50 -mx-6 -mt-6 px-6 py-4 rounded-t-lg sticky top-0 z-10">
                <DialogTitle className="text-2xl font-bold text-green-800 flex items-center justify-between">
                  Editar Producto Recomendado
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditProducto(false);
                      setEditingProducto(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateProducto} className="space-y-4 mt-4 pt-4 px-1">
                <div className="space-y-2">
                  <Label htmlFor="edit-fecha" className="text-base font-semibold">Fecha de Visita</Label>
                  <Input
                    id="edit-fecha"
                    type="date"
                    value={productoForm.fecha_visita}
                    onChange={(e) => setProductoForm({...productoForm, fecha_visita: e.target.value})}
                    required
                    className="text-base py-6 border-2"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-nombre" className="text-base font-semibold">Nombre del Producto</Label>
                  <Input
                    id="edit-nombre"
                    value={productoForm.nombre}
                    onChange={(e) => setProductoForm({...productoForm, nombre: e.target.value})}
                    required
                    className="text-base py-6 border-2"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-dosis" className="text-base font-semibold">Dosis</Label>
                  <Input
                    id="edit-dosis"
                    value={productoForm.dosis}
                    onChange={(e) => setProductoForm({...productoForm, dosis: e.target.value})}
                    className="text-base py-6 border-2"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-indicaciones" className="text-base font-semibold">Indicaciones</Label>
                  <Input
                    id="edit-indicaciones"
                    value={productoForm.indicaciones}
                    onChange={(e) => setProductoForm({...productoForm, indicaciones: e.target.value})}
                    className="text-base py-6 border-2"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-notas" className="text-base font-semibold">Notas</Label>
                  <Input
                    id="edit-notas"
                    value={productoForm.notas}
                    onChange={(e) => setProductoForm({...productoForm, notas: e.target.value})}
                    className="text-base py-6 border-2"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowEditProducto(false);
                      setEditingProducto(null);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 pharmacy-button text-white"
                  >
                    Actualizar Producto
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientEvolutionModal;